import type { OpUnitType } from "dayjs";
import type { XAxisPlotLinesOptions } from "highcharts";
import type { ReactNode } from "react";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import type { TimeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import { translate } from "app/filters/i18nFilter";
import { granularityNameToMomentUnitSingular } from "app/UtilitiesAndConstants/Constants/Moment";
import dayjs from "single-spa/dayjs-extend";

import ChangeBellWithoutTooltip, { Marker, StyledAlertIcon } from "./ChangeBellWithoutTooltip";

type DisplayedPeriod = string | number | null | undefined;

// The shared map's Weekly unit is dayjs's Sunday-based "week", but the API's buckets start Monday.
const granularityToUnit: Record<TimeGranularity, OpUnitType | "isoWeek"> = {
    ...granularityNameToMomentUnitSingular,
    Weekly: "isoWeek",
};

const AFFECTED_CHANNELS = ["organicsearch", "searchorganic", "genai"] as const;
type AffectedChannel = (typeof AFFECTED_CHANNELS)[number];

// Channel names reach us in several shapes ("Organic Search", "Search - Organic", "searchOrganic"),
// so strip separators and case before comparing. Not using trafficSourcesForSegments[...].primaryKey:
// Gen AI is mis-keyed there as "Social".
const normalizeChannelName = (channel: string): string => channel.replace(/[^a-z0-9]/gi, "").toLowerCase();

const isAffectedChannel = (normalizedChannel: string): normalizedChannel is AffectedChannel =>
    (AFFECTED_CHANNELS as readonly string[]).includes(normalizedChannel);

export const isGoogleAiModeAffectedChannel = (channel: string | null | undefined): boolean =>
    typeof channel === "string" && isAffectedChannel(normalizeChannelName(channel));

const getGoogleAiModeChanges = () => {
    const resources = swSettings.components?.Home?.resources;
    return [
        [
            resources?.GoogleAiModeGenAiClassificationStartDate,
            "analysis.ai.traffic.annotation.classified.as.genai",
        ] as const,
        [
            resources?.GoogleAiModeOrganicSearchReclassificationStartDate,
            "analysis.ai.traffic.annotation.reclassified.as.organic.search",
        ] as const,
    ];
};

const isSamePeriod = (period: DisplayedPeriod, date: string | undefined, granularity: TimeGranularity): boolean => {
    if (period == null || typeof date !== "string") {
        return false;
    }
    const periodDate = dayjs.utc(period);
    const changeDate = dayjs.utc(date);
    return (
        periodDate.isValid() && changeDate.isValid() && periodDate.isSame(changeDate, granularityToUnit[granularity])
    );
};

export const getGoogleAiModeAnnotationPlotLines = (
    displayedPeriods: readonly DisplayedPeriod[],
    granularity: TimeGranularity = "Monthly",
    isCategoryAxis = false,
): XAxisPlotLinesOptions[] =>
    getGoogleAiModeChanges().flatMap(([date]) => {
        const index = displayedPeriods.findIndex((period) => isSamePeriod(period, date, granularity));
        if (index === -1) {
            return [];
        }
        const value = isCategoryAxis ? index : dayjs.utc(displayedPeriods[index]).valueOf();
        return [ChangeBellWithoutTooltip(value, true).xAxis.plotLines[0] as XAxisPlotLinesOptions];
    });

const BannerWrapper = styled(Flex)`
    background-color: ${colorsPalettes.orange[100]};
    min-height: 44px;
    margin-top: 16px;
    padding: 8px 16px;
    border-radius: 4px;
`;

const BellMarker = styled(Marker)`
    flex-shrink: 0;
    min-width: 20px;
`;

const AnnotationBanner = ({ text }: { text: string }) => (
    <BannerWrapper
        alignItems="center"
        gap="8px"
        data-automation="google-ai-mode-annotation-banner"
    >
        <BellMarker>
            <StyledAlertIcon iconName="alerts" />
        </BellMarker>
        <Text
            fontVariant="report"
            color={colorsPalettes.carbon[300]}
            flex="1"
            minWidth={0}
            whiteSpace="normal"
        >
            {text}
        </Text>
    </BannerWrapper>
);

export const getGoogleAiModeAnnotationBanner = (
    hoveredPeriod: DisplayedPeriod,
    granularity: TimeGranularity = "Monthly",
): ReactNode | undefined => {
    const change = getGoogleAiModeChanges().find(([date]) => isSamePeriod(hoveredPeriod, date, granularity));
    return change ? <AnnotationBanner text={translate(change[1])} /> : undefined;
};
